import type { ClinicDetail, HealthState } from '@pa/api/admin'
import { heartbeatText, STATE_LABEL, useNow } from '@pa/staff'
import { Circle, CircleCheck } from 'lucide-react'
import { timeZoneLabel } from '@/features/clinics'
import { PfMessagesSwitchCard } from './PfMessagesSwitchCard'
import { TestingSwitchCard } from './TestingSwitchCard'

function ChecklistItem({ done, label }: { done: boolean; label: string }) {
  return (
    <li className={done ? 'ok' : 'no'}>
      {done ? <CircleCheck aria-hidden /> : <Circle aria-hidden />}
      <span>
        {label}
        <span className="sr-only">{done ? ': done' : ': not yet'}</span>
      </span>
    </li>
  )
}

const when = (iso: string | null) =>
  iso === null ? 'Never' : new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })

/** The snapshot's line for one component: Operational / Degraded / Down / No data (C-12's words). */
function componentText(clinic: ClinicDetail, state: HealthState): string {
  if (clinic.pf.status === 'inactive') return 'Inactive'
  if (clinic.pf.status === 'not_connected') return 'Not connected'
  return STATE_LABEL[state]
}

function HeartbeatText({ clinic }: { clinic: ClinicDetail }) {
  const now = useNow()
  if (clinic.pf.status === 'inactive' || clinic.pf.status === 'not_connected') return <>—</>
  return <>{heartbeatText(clinic.pf.lastHeartbeatAt, now).replace(/^Last heartbeat /, '')}</>
}

/**
 * S-04 Summary (UserFlows S-04; API.md §7.4): the health snapshot (PF Keeper, PF session, last heartbeat; the §5.6
 * state from #40 `pf`), bookings in the last 7 / 30 days, errors in the last 24 h / 7 days (`error_events` rows, the
 * same counts as C-03), the last PF sync, the setup checklist and the "Testing only" switch.
 */
export function SummaryTab({ clinic }: { clinic: ClinicDetail }) {
  return (
    <div className="grid two">
      <section className="card card-pad form-grid" aria-labelledby="s04-setup">
        <h2 className="form-section-title" id="s04-setup">
          Setup checklist
        </h2>
        <ul className="check-items">
          <ChecklistItem done={clinic.setup.connected} label="PF Keeper connected" />
          <ChecklistItem done={clinic.setup.firstAdminSetPassword} label="First admin has set their password" />
          <ChecklistItem done={clinic.setup.hasBookableVisitType} label="At least one bookable visit type" />
        </ul>
      </section>
      <section className="card card-pad form-grid" aria-labelledby="s04-health">
        <h2 className="form-section-title" id="s04-health">
          Health
        </h2>
        <dl className="kv">
          <dt>PF Keeper</dt>
          <dd>{componentText(clinic, clinic.pf.pfKeeperApp)}</dd>
          <dt>PF session</dt>
          <dd>{componentText(clinic, clinic.pf.pfSession)}</dd>
          <dt>Last heartbeat</dt>
          <dd>
            <HeartbeatText clinic={clinic} />
          </dd>
        </dl>
      </section>
      <section className="card card-pad form-grid" aria-labelledby="s04-activity">
        <h2 className="form-section-title" id="s04-activity">
          Bookings and errors
        </h2>
        <dl className="kv">
          <dt>Bookings, 7 days</dt>
          <dd>{clinic.bookings7d}</dd>
          <dt>Bookings, 30 days</dt>
          <dd>{clinic.bookings30d}</dd>
          <dt>Errors, 24 hours</dt>
          <dd>{clinic.errors24h}</dd>
          <dt>Errors, 7 days</dt>
          <dd>{clinic.errors7d}</dd>
        </dl>
      </section>
      <section className="card card-pad form-grid" aria-labelledby="s04-settings">
        <h2 className="form-section-title" id="s04-settings">
          Clinic
        </h2>
        <dl className="kv">
          <dt>Time zone</dt>
          <dd>{timeZoneLabel(clinic.timeZone)}</dd>
          <dt>Last PF sync</dt>
          <dd>{when(clinic.lastSyncedAt)}</dd>
          <dt>Users</dt>
          <dd>{clinic.usersCount}</dd>
        </dl>
      </section>
      <PfMessagesSwitchCard clinic={clinic} />
      <TestingSwitchCard clinic={clinic} />
    </div>
  )
}
